import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  MenuItem,
  IconButton,
  Tooltip,
  LinearProgress,
  Grid,
  Collapse,
  Link,
} from '@mui/material';
import {
  CheckCircleOutline,
  Close,
  TrendingUp,
  Speed,
  Build,
  Visibility,
  LightbulbOutlined,
  WarningAmber,
  ErrorOutline,
  InfoOutlined,
  ExpandMore,
  ExpandLess,
  OpenInNew,
  TrendingDown,
  TrendingFlat,
  Star,
} from '@mui/icons-material';
import { fetchRecommendations, dismissRecommendation, implementRecommendation, type RecommendationsResponse } from '../../services/analytics.service';

export default function RecommendationsAnalyticsPage() {
  const [pageId, _setPageId] = useState<number | ''>('');
  const [tenantId, _setTenantId] = useState<number | ''>('');
  const [status, setStatus] = useState<'pending' | 'implemented' | 'dismissed' | ''>('');
  const [priority, setPriority] = useState<'high' | 'medium' | 'low' | ''>('');
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<RecommendationsResponse | null>(null);
  const [expandedItems, setExpandedItems] = useState<Set<string | number>>(new Set());

  const toggleExpand = (id: string | number) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchRecommendations({
        page_id: pageId === '' ? undefined : pageId,
        tenant_id: tenantId === '' ? undefined : tenantId,
        status: status === '' ? undefined : status,
        priority: priority === '' ? undefined : priority,
        limit: 100,
      });
      setData(res.data);
    } catch (e: any) {
      setError(e?.message || 'Failed to load recommendations');
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = async (id: string | number) => {
    setActing(String(id));
    try {
      await dismissRecommendation(id);
      setData((prev) => {
        const items = prev?.recommendations || prev?.items || [];
        const updated = items.map((r) => (
          String(r.id) === String(id) ? { ...r, status: 'dismissed' as const } : r
        ));
        return {
          ...prev!,
          recommendations: updated,
          items: updated,
        };
      });
    } catch (e: any) {
      setError(e?.message || 'Failed to dismiss recommendation');
    } finally {
      setActing(null);
    }
  };

  const handleImplement = async (id: string | number) => {
    setActing(String(id));
    try {
      await implementRecommendation(id);
      setData((prev) => {
        const items = prev?.recommendations || prev?.items || [];
        const updated = items.map((r) => (
          String(r.id) === String(id) ? { ...r, status: 'implemented' as const } : r
        ));
        return {
          ...prev!,
          recommendations: updated,
          items: updated,
        };
      });
    } catch (e: any) {
      setError(e?.message || 'Failed to implement recommendation');
    } finally {
      setActing(null);
    }
  };

  const priorityIcon = (p?: string) => {
    if (p === 'high') return <ErrorOutline color="error" />;
    if (p === 'medium') return <WarningAmber color="warning" />;
    return <InfoOutlined color="success" />;
  };

  const priorityLabel = (p?: string) => {
    if (p === 'high') return 'Review Soon';
    if (p === 'medium') return 'When You Can';
    return 'Nice to Have';
  };

  const typeIcon = (t?: string) => {
    if (t === 'seo') return <Visibility />;
    if (t === 'content') return <Build />;
    if (t === 'performance') return <Speed />;
    return <LightbulbOutlined />;
  };

  const statusColor = (s?: string) => {
    if (s === 'implemented') return 'success';
    if (s === 'dismissed') return 'default';
    return 'info';
  };

  const statusLabel = (s?: string) => {
    if (s === 'implemented') return '✅ Done';
    if (s === 'dismissed') return '⏭️ Skipped';
    return '📋 To Review';
  };

  // Auto-load on mount
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Header */}
      <Box>
        <Typography variant="h5" gutterBottom>
          🎯 Recommendations
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Items detected that may need your attention. Review and mark as done when addressed.
        </Typography>
      </Box>

      {/* Quick Stats */}
      {data && (
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.50', border: '1px solid', borderColor: 'error.200' }}>
              <Typography variant="h4" color="error.main">
                {(data.recommendations || data.items || []).filter(r => {
                  const isActive = r.status === 'pending';
                  return (r.priority === 'high' || r.impact_estimate === 'HIGH') && isActive;
                }).length || 0}
              </Typography>
              <Typography variant="caption" color="error.main">Review Soon</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.200' }}>
              <Typography variant="h4" color="warning.main">
                {(data.recommendations || data.items || []).filter(r => {
                  const isActive = r.status === 'pending';
                  return (r.priority === 'medium' || r.impact_estimate === 'MEDIUM') && isActive;
                }).length || 0}
              </Typography>
              <Typography variant="caption" color="warning.main">When You Can</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200' }}>
              <Typography variant="h4" color="success.main">
                {(data.recommendations || data.items || []).filter(r => r.status === 'implemented').length || 0}
              </Typography>
              <Typography variant="caption" color="success.main">Completed</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.100', border: '1px solid', borderColor: 'grey.300' }}>
              <Typography variant="h4" color="text.secondary">
                {(data.recommendations || data.items || []).filter(r => r.status === 'dismissed').length || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">Skipped</Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Filters - Simplified */}
      <Card variant="outlined">
        <CardContent sx={{ py: 1.5 }}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <TextField
              select
              label="Show"
              size="small"
              value={status}
              onChange={(e) => { setStatus(e.target.value as any); }}
              sx={{ width: 160 }}
            >
              <MenuItem value="">All Items</MenuItem>
              <MenuItem value="pending">📋 To Review</MenuItem>
              <MenuItem value="implemented">✅ Completed</MenuItem>
              <MenuItem value="dismissed">⏭️ Skipped</MenuItem>
            </TextField>
            <TextField
              select
              label="Priority"
              size="small"
              value={priority}
              onChange={(e) => { setPriority(e.target.value as any); }}
              sx={{ width: 160 }}
            >
              <MenuItem value="">All Priorities</MenuItem>
              <MenuItem value="high">🔴 Review Soon</MenuItem>
              <MenuItem value="medium">🟡 When You Can</MenuItem>
              <MenuItem value="low">🟢 Nice to Have</MenuItem>
            </TextField>
            <Button variant="outlined" onClick={load} disabled={loading} size="small">
              {loading ? <CircularProgress size={16} /> : 'Refresh'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Error */}
      {error && <Alert severity="error">{error}</Alert>}

      {/* Loading */}
      {loading && <LinearProgress />}

      {/* Items as Cards */}
      {data && !loading && (
        <Stack spacing={2}>
          {(!(data.recommendations || data.items) || (data.recommendations || data.items || []).length === 0) ? (
            <Alert severity="success" icon={<CheckCircleOutline />}>
              <Typography variant="subtitle2">All caught up!</Typography>
              <Typography variant="body2">No items need your attention right now.</Typography>
            </Alert>
          ) : (
            (data.recommendations || data.items || [])
              .filter(rec => {
                if (status === '') return true;
                return rec.status === status;
              })
              .filter(rec => {
                if (priority === '') return true;
                if (priority === 'high') return rec.priority === 'high' || rec.impact_estimate === 'HIGH';
                if (priority === 'medium') return rec.priority === 'medium' || rec.impact_estimate === 'MEDIUM';
                if (priority === 'low') return rec.priority === 'low' || rec.impact_estimate === 'LOW';
                return rec.priority === priority;
              })
              .map((rec) => {
                const isExpanded = expandedItems.has(rec.id!);
                const evidence = rec.evidence;
                const relatedMetrics = rec.related_metrics;
                const actionItems = rec.action_items;
                const impactEstimate = rec.impact_estimate;
                const confidenceScore = rec.confidence_score;
                const pageUrl = rec.page_url;
                const recType = rec.recommendation_type || rec.type;
                
                // Derive priority from impact_estimate if not set
                const derivedPriority = rec.priority || (impactEstimate === 'HIGH' ? 'high' : impactEstimate === 'MEDIUM' ? 'medium' : 'low');
                
                return (
              <Card 
                key={rec.id} 
                variant="outlined"
                sx={{ 
                  borderLeft: 4, 
                  borderLeftColor: rec.status === 'implemented' ? 'success.main' : 
                                   rec.status === 'dismissed' ? 'grey.400' :
                                   derivedPriority === 'high' ? 'error.main' : 
                                   derivedPriority === 'medium' ? 'warning.main' : 'success.main',
                  opacity: rec.status === 'dismissed' || rec.status === 'implemented' ? 0.7 : 1,
                }}
              >
                <CardContent sx={{ py: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    {/* Left: Content */}
                    <Stack direction="row" spacing={2} alignItems="flex-start" flex={1}>
                      {/* Priority Icon */}
                      <Box sx={{ pt: 0.5 }}>
                        {rec.status === 'implemented' ? (
                          <CheckCircleOutline color="success" sx={{ fontSize: 28 }} />
                        ) : rec.status === 'dismissed' ? (
                          <Close color="disabled" sx={{ fontSize: 28 }} />
                        ) : (
                          priorityIcon(derivedPriority)
                        )}
                      </Box>

                      {/* Text Content */}
                      <Box flex={1}>
                        <Stack direction="row" spacing={1} alignItems="center" mb={0.5} flexWrap="wrap">
                          <Typography variant="subtitle1" fontWeight="medium">
                            {rec.title ?? 'Review Item'}
                          </Typography>
                          <Chip 
                            label={statusLabel(rec.status)} 
                            size="small" 
                            variant="outlined"
                            color={statusColor(rec.status) as any}
                          />
                          {impactEstimate && (
                            <Chip 
                              icon={<Star sx={{ fontSize: 14 }} />}
                              label={`${impactEstimate} Impact`}
                              size="small"
                              color={impactEstimate === 'HIGH' ? 'error' : impactEstimate === 'MEDIUM' ? 'warning' : 'default'}
                              variant="filled"
                              sx={{ fontWeight: 'bold' }}
                            />
                          )}
                        </Stack>
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {rec.description ?? 'No additional details available.'}
                        </Typography>

                        {/* Page URL */}
                        {pageUrl && (
                          <Box sx={{ mb: 1.5 }}>
                            <Link 
                              href={pageUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              sx={{ 
                                display: 'inline-flex', 
                                alignItems: 'center', 
                                gap: 0.5,
                                fontSize: '0.875rem',
                                color: 'primary.main',
                                textDecoration: 'none',
                                '&:hover': { textDecoration: 'underline' }
                              }}
                            >
                              <OpenInNew sx={{ fontSize: 14 }} />
                              {pageUrl.length > 60 ? pageUrl.substring(0, 60) + '...' : pageUrl}
                            </Link>
                          </Box>
                        )}

                        {/* Evidence Summary - Always Visible */}
                        {evidence && (
                          <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: 'grey.50' }}>
                            <Grid container spacing={2}>
                              <Grid item xs={4}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Opportunity Score
                                </Typography>
                                <Typography variant="h6" color="primary.main" fontWeight="bold">
                                  {evidence.opportunity_score?.toFixed(1) ?? '-'}
                                </Typography>
                              </Grid>
                              <Grid item xs={4}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  OCI Score
                                </Typography>
                                <Typography 
                                  variant="h6" 
                                  color={(evidence.oci_score ?? 0) < 0 ? 'error.main' : 'success.main'}
                                  fontWeight="bold"
                                >
                                  {evidence.oci_score?.toFixed(1) ?? '-'}
                                </Typography>
                              </Grid>
                              <Grid item xs={4}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Gap to Close
                                </Typography>
                                <Typography variant="h6" color="warning.main" fontWeight="bold">
                                  {evidence.gap?.toFixed(1) ?? '-'}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Paper>
                        )}

                        {/* Meta info */}
                        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                          <Chip 
                            icon={typeIcon(recType)} 
                            label={recType?.replace('_', ' ').toLowerCase() || 'general'} 
                            size="small" 
                            variant="outlined"
                            sx={{ textTransform: 'capitalize' }}
                          />
                          {confidenceScore && (
                            <Chip
                              label={`${confidenceScore}% confidence`}
                              size="small"
                              variant="outlined"
                              color="info"
                            />
                          )}
                          {(rec.status === 'pending') && (
                            <Typography variant="caption" color="text.secondary">
                              {priorityLabel(derivedPriority)}
                            </Typography>
                          )}
                          {/* Expand/Collapse Button */}
                          {(actionItems?.length || relatedMetrics) && (
                            <Button
                              size="small"
                              onClick={() => toggleExpand(rec.id!)}
                              endIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
                              sx={{ ml: 'auto' }}
                            >
                              {isExpanded ? 'Less Details' : 'More Details'}
                            </Button>
                          )}
                        </Stack>

                        {/* Expandable Section */}
                        <Collapse in={isExpanded}>
                          <Box sx={{ mt: 2 }}>
                            {/* Action Items */}
                            {actionItems && actionItems.length > 0 && (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <LightbulbOutlined fontSize="small" color="warning" />
                                  Action Items
                                </Typography>
                                <Stack spacing={0.5}>
                                  {actionItems.map((item, idx) => (
                                    <Typography 
                                      key={idx} 
                                      variant="body2" 
                                      sx={{ 
                                        pl: 2, 
                                        position: 'relative',
                                        '&::before': {
                                          content: '"•"',
                                          position: 'absolute',
                                          left: 0,
                                          color: 'primary.main'
                                        }
                                      }}
                                    >
                                      {item}
                                    </Typography>
                                  ))}
                                </Stack>
                              </Box>
                            )}

                            {/* Related Metrics */}
                            {relatedMetrics && (
                              <Grid container spacing={2}>
                                {/* OCI Breakdown */}
                                {relatedMetrics.oci && (
                                  <Grid item xs={12} md={6}>
                                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                                      <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Speed fontSize="small" color="primary" />
                                        OCI Breakdown
                                        <Chip 
                                          label={relatedMetrics.oci.oci_level} 
                                          size="small" 
                                          color={relatedMetrics.oci.oci_level === 'LOW' ? 'error' : relatedMetrics.oci.oci_level === 'HIGH' ? 'success' : 'warning'}
                                          sx={{ ml: 'auto' }}
                                        />
                                      </Typography>
                                      <Stack spacing={0.5}>
                                        {relatedMetrics.oci.factors && Object.entries(relatedMetrics.oci.factors).map(([key, value]) => {
                                          if (key === 'momentum_state') {
                                            return (
                                              <Stack key={key} direction="row" justifyContent="space-between" alignItems="center">
                                                <Typography variant="caption" color="text.secondary">
                                                  {key.replace('_', ' ')}
                                                </Typography>
                                                <Chip
                                                  size="small"
                                                  icon={value === 'IMPROVING' ? <TrendingUp /> : value === 'DECLINING' ? <TrendingDown /> : <TrendingFlat />}
                                                  label={String(value)}
                                                  color={value === 'IMPROVING' ? 'success' : value === 'DECLINING' ? 'error' : 'default'}
                                                />
                                              </Stack>
                                            );
                                          }
                                          const numVal = Number(value);
                                          return (
                                            <Stack key={key} direction="row" justifyContent="space-between">
                                              <Typography variant="caption" color="text.secondary">
                                                {key.replace('_', ' ')}
                                              </Typography>
                                              <Typography 
                                                variant="caption" 
                                                fontWeight="bold"
                                                color={numVal > 0 ? 'success.main' : numVal < 0 ? 'error.main' : 'text.primary'}
                                              >
                                                {numVal > 0 ? '+' : ''}{numVal.toFixed(1)}%
                                              </Typography>
                                            </Stack>
                                          );
                                        })}
                                      </Stack>
                                    </Paper>
                                  </Grid>
                                )}

                                {/* Opportunity Breakdown */}
                                {relatedMetrics.opportunity && (
                                  <Grid item xs={12} md={6}>
                                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                                      <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <TrendingUp fontSize="small" color="success" />
                                        Opportunity Metrics
                                        <Chip 
                                          label={relatedMetrics.opportunity.opportunity_level} 
                                          size="small" 
                                          color={relatedMetrics.opportunity.opportunity_level === 'HIGH' ? 'success' : relatedMetrics.opportunity.opportunity_level === 'LOW' ? 'error' : 'warning'}
                                          sx={{ ml: 'auto' }}
                                        />
                                      </Typography>
                                      <Stack spacing={0.5}>
                                        <Stack direction="row" justifyContent="space-between">
                                          <Typography variant="caption" color="text.secondary">Avg Position</Typography>
                                          <Typography variant="caption" fontWeight="bold">
                                            {relatedMetrics.opportunity.avg_position?.toFixed(1)}
                                          </Typography>
                                        </Stack>
                                        <Stack direction="row" justifyContent="space-between">
                                          <Typography variant="caption" color="text.secondary">Avg CTR</Typography>
                                          <Typography variant="caption" fontWeight="bold">
                                            {((relatedMetrics.opportunity.avg_ctr ?? 0) * 100).toFixed(2)}%
                                          </Typography>
                                        </Stack>
                                        <Stack direction="row" justifyContent="space-between">
                                          <Typography variant="caption" color="text.secondary">Avg Impressions</Typography>
                                          <Typography variant="caption" fontWeight="bold">
                                            {relatedMetrics.opportunity.avg_impressions?.toLocaleString(undefined, {maximumFractionDigits: 0})}
                                          </Typography>
                                        </Stack>
                                      </Stack>
                                    </Paper>
                                  </Grid>
                                )}
                              </Grid>
                            )}
                          </Box>
                        </Collapse>
                      </Box>
                    </Stack>

                    {/* Right: Actions */}
                    {rec.status === 'pending' && (
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Mark as done">
                          <IconButton 
                            color="success" 
                            onClick={() => handleImplement(rec.id!)}
                            disabled={acting === String(rec.id)}
                            size="small"
                            sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' } }}
                          >
                            <CheckCircleOutline />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Skip this">
                          <IconButton 
                            onClick={() => handleDismiss(rec.id!)}
                            disabled={acting === String(rec.id)}
                            size="small"
                            sx={{ bgcolor: 'grey.100', '&:hover': { bgcolor: 'grey.200' } }}
                          >
                            <Close />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                  </Stack>
                </CardContent>
              </Card>
              );
            })
          )}
        </Stack>
      )}

      {/* Data Source Attribution */}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
        📊 Source: Automated analysis based on GSC metrics, SEO validation, and page performance data.
      </Typography>
    </Box>
  );
}
